/** The backend's backstop behind useEasyGoLiveGate: 409 `easy_go_live_required`
 *  (ConsoleEasyGoLiveRequired `{detail, code, tour_operator_ids}`).
 *
 *  An easy-onboarding tenant still under the outbound guard changes its launch policy only
 *  with Go live in its easy setup, which lifts the guard and sets the policy together. The
 *  backend refuses every other launch-policy change for such a tenant: the tenant default (the
 *  tour-operator PATCH, beside `config_modified`), a tour's own policy (tour create and PATCH,
 *  beside `tour_modified`), and the Tours grid's batch Set launch policy (refused as a whole,
 *  nothing queued — the selection may span tenants, so `tour_operator_ids` names every one).
 *
 *  Read here and said in one set of words, with a link to each tenant's easy setup — never as
 *  a generic error, and the caller keeps what the operator chose. Nothing was changed. */
import { ConsoleApiError } from '@/api/client';
import type { components } from '@/api/types';
import type { BatchRefusal, BatchRefusals } from '@/components/DataGrid/batchTypes';

import { easyOnboardHref } from './useEasyGoLiveGate';

export type EasyGoLiveRequiredBody = components['schemas']['ConsoleEasyGoLiveRequired'];

export const EASY_GO_LIVE_REQUIRED: EasyGoLiveRequiredBody['code'] = 'easy_go_live_required';

export interface EasyGoLiveRequired {
  /** The tenants that must go live first (deduplicated, in the server's order). */
  tourOperatorIds: number[];
}

/** The refusal behind a failed launch-policy change, or null for any other error (a
 *  `config_modified` / `tour_modified` 409 included). */
export function easyGoLiveRequired(e: unknown): EasyGoLiveRequired | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 409) return null;
  // Typed as an object, but a proxy's HTML error page arrives as a string.
  const raw: unknown = e.body;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const body = raw as Record<string, unknown>;
  if (body.code !== EASY_GO_LIVE_REQUIRED) return null;
  const ids = Array.isArray(body.tour_operator_ids)
    ? body.tour_operator_ids.filter((id): id is number => typeof id === 'number')
    : [];
  return { tourOperatorIds: [...new Set(ids)] };
}

/** The refusal for one tenant's launch policy (the tenant default, a tour's own). */
export function easyGoLiveRequiredWords(ids: readonly number[]): string {
  if (ids.length > 1) {
    return (
      `${ids.length} tenants here were set up with easy onboarding and their automatic guest ` +
      'messages are still blocked. Each sets its launch policy when it goes live from its easy ' +
      'setup, which lifts the block and sets the policy together.'
    );
  }
  return (
    'This tenant was set up with easy onboarding and its automatic guest messages are still ' +
    'blocked. It sets its launch policy when it goes live from its easy setup, which lifts the ' +
    'block and sets the policy together.'
  );
}

export interface EasyGoLiveLink {
  id: number;
  to: string;
  label: string;
}

/** "Go live in easy setup" per tenant; named by id when there is more than one. */
export function easyGoLiveLinks(ids: readonly number[]): EasyGoLiveLink[] {
  return ids.map((id) => ({
    id,
    to: easyOnboardHref(id),
    label: ids.length === 1 ? 'Go live in easy setup' : `Go live in easy setup · tenant #${id}`,
  }));
}

/** The Tours grid's batch Set launch policy refused for the whole selection. */
export function easyGoLiveBatchRefusal(e: unknown): BatchRefusal | null {
  const refused = easyGoLiveRequired(e);
  if (!refused) return null;
  const ids = refused.tourOperatorIds;
  const who =
    ids.length === 1
      ? 'One tenant in the selection was set up with easy onboarding and its automatic guest messages are still blocked'
      : `${ids.length} tenants in the selection were set up with easy onboarding and their automatic guest messages are still blocked`;
  return {
    title: 'Can’t set the launch policy',
    detail:
      `${who}. ${ids.length === 1 ? 'It sets' : 'Each sets'} its launch policy when it goes live ` +
      'from its easy setup, which lifts the block and sets the policy together. Nothing was ' +
      'changed and the selection is kept: go live first, or leave those tenants’ tours out.',
    items: ids.map((id) => ({ id, reason: 'go live in its easy setup', to: easyOnboardHref(id) })),
  };
}

/** The batch Set launch policy's refusal reporting. The server refuses it as a whole, never
 *  tour by tour, so there is nothing to add about tours left out. */
export const easyGoLiveBatchRefusals: BatchRefusals = {
  leftOut: () => '',
  refused: easyGoLiveBatchRefusal,
};
